import { RECORDS_CAPACITY_LABEL } from '../customer/strings/records-access';

export type Capacity = 'team' | 'attorney' | 'customer' | 'customer_admin' | 'customer_records' | 'customer_readonly' | 'practice_preview' | 'provider';

const key = (customerId: string) => `verityrx-capacity:${customerId}`;
const releasePreviewKey = (customerId: string) => `verityrx-release-preview:${customerId}`;
const homeKey = 'verityrx-home-capacity';

export function preferredHomeCapacity(): 'team' | 'attorney' | null {
  const value = sessionStorage.getItem(homeKey);
  return value === 'team' || value === 'attorney' ? value : null;
}

export function rememberHomeCapacity(capacity: Capacity): void {
  if (capacity === 'team' || capacity === 'attorney') sessionStorage.setItem(homeKey, capacity);
}

export function releasePreview(customerId: string): boolean {
  return new URLSearchParams(window.location.search).get('practice_preview') === 'release' ||
    sessionStorage.getItem(releasePreviewKey(customerId)) === '1';
}

export function rememberReleasePreview(customerId: string): void {
  sessionStorage.setItem(releasePreviewKey(customerId), '1');
}

export function clearReleasePreview(customerId: string): void {
  sessionStorage.removeItem(releasePreviewKey(customerId));
}

export function selectedCapacity(customerId: string): Capacity | null {
  const value = sessionStorage.getItem(key(customerId));
  return value === 'team' || value === 'attorney' || value === 'customer' ||
    value === 'customer_admin' || value === 'customer_records' || value === 'customer_readonly' ||
    value === 'practice_preview' ? value : null;
}

export function chooseCapacity(customerId: string, capacity: Capacity): void {
  sessionStorage.setItem(key(customerId), capacity);
}

// Null leaves the server to resolve the route's ordinary capacity.
export function capacityForRoute(pathname: string, stored: Capacity | null): Capacity | null {
  const route = pathname.match(/^\/(customers|c|portal|provider)\/[0-9a-f-]{36}(?:\/|$)/i)?.[1];
  if (route === 'provider') return 'provider';
  if (route === 'customers') return 'team';
  if (route === 'c') return stored === 'team' || stored === 'attorney' ? stored : null;
  if (route === 'portal') {
    return stored === 'team' || stored === 'customer' || stored === 'customer_admin' ||
      stored === 'customer_records' || stored === 'customer_readonly' || stored === 'practice_preview' ? stored : null;
  }
  return null;
}

/** Identity and own-grant probes run before route capacity is resolved; they
 * must not carry a provider header that hides those endpoints. */
export function capacityForRequest(
  url: string | undefined,
  pathname: string,
  stored: Capacity | null,
): Capacity | null {
  const requestPath = url?.split('?')[0];
  if (requestPath === '/me' || requestPath === '/my-customers') return null;
  return capacityForRoute(pathname, stored);
}

// True when the page should read the team's operator routes: the request's capacity (what the
// client sends for this route) is Team, or nothing is stored and the person is a team admin.
// A practice capacity, a read-only preview and the release preview all read the practice's own routes.
export function operatorViewFor(customerId: string, pathname: string, isInternalAdmin: boolean): boolean {
  if (releasePreview(customerId)) return false;
  const capacity = capacityForRoute(pathname, selectedCapacity(customerId));
  if (capacity === null) return isInternalAdmin;
  return capacity === 'team';
}

export const capacityLabel: Record<Capacity, string> = {
  team: 'Team',
  attorney: 'Reviewer',
  customer: 'The practice',
  customer_admin: "The practice's admin",
  customer_records: RECORDS_CAPACITY_LABEL,
  customer_readonly: 'The practice (view only)',
  practice_preview: 'The practice (preview, read-only)',
  provider: 'Provider',
};
